// File: frontend/src/components/common/ui/ActionsDropdown.tsx
// Reusable actions dropdown component for all listing pages

import React, { useState, useRef, useEffect } from 'react';
import { 
  MoreVertical, 
  Edit, 
  Eye, 
  Trash2, 
  ToggleLeft, 
  ToggleRight, 
  Lock, 
  Unlock,
  UserCheck,
  UserX,
  Settings,
  Copy,
  Download,
  Mail,
  Phone,
  Star,
  StarOff,
  CheckCircle,
  XCircle,
  RefreshCw
} from 'lucide-react';

export interface ActionItem {
  id: string;
  label: string;
  icon?: React.ComponentType<{ size?: number; className?: string }>;
  onClick: () => void;
  variant?: 'default' | 'primary' | 'secondary' | 'success' | 'warning' | 'danger';
  disabled?: boolean;
  hidden?: boolean;
  separator?: boolean; // Add a separator before this action
}

interface ActionsDropdownProps {
  actions: ActionItem[];
  size?: 'sm' | 'md' | 'lg';
  className?: string;
  disabled?: boolean;
}

const iconMap = {
  edit: Edit,
  view: Eye,
  delete: Trash2,
  toggle: ToggleLeft,
  'toggle-on': ToggleRight,
  'toggle-off': ToggleLeft,
  lock: Lock,
  unlock: Unlock,
  activate: UserCheck,
  deactivate: UserX,
  settings: Settings,
  copy: Copy,
  download: Download,
  email: Mail,
  phone: Phone,
  feature: Star,
  unfeature: StarOff,
  approve: CheckCircle,
  reject: XCircle,
  refresh: RefreshCw,
};

export const ActionsDropdown: React.FC<ActionsDropdownProps> = ({
  actions,
  size = 'md',
  className = '',
  disabled = false
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  // Filter out hidden actions
  const visibleActions = actions.filter(action => !action.hidden);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleActionClick = (action: ActionItem) => {
    if (!action.disabled) {
      action.onClick();
      setIsOpen(false);
    }
  };

  const getVariantClasses = (variant: string = 'default') => {
    const variants = {
      default: 'text-gray-700 hover:bg-gray-50 hover:text-gray-900',
      primary: 'text-blue-700 hover:bg-blue-50 hover:text-blue-900',
      secondary: 'text-purple-700 hover:bg-purple-50 hover:text-purple-900',
      success: 'text-green-700 hover:bg-green-50 hover:text-green-900',
      warning: 'text-yellow-700 hover:bg-yellow-50 hover:text-yellow-900',
      danger: 'text-red-700 hover:bg-red-50 hover:text-red-900',
    };
    return variants[variant as keyof typeof variants] || variants.default;
  };

  const getSizeClasses = () => {
    const sizes = {
      sm: {
        button: 'p-1',
        icon: 'w-4 h-4',
        dropdown: 'w-40',
        item: 'px-2 py-1.5 text-xs'
      },
      md: {
        button: 'p-1.5',
        icon: 'w-4 h-4',
        dropdown: 'w-48',
        item: 'px-3 py-2 text-sm'
      },
      lg: {
        button: 'p-2',
        icon: 'w-5 h-5',
        dropdown: 'w-56',
        item: 'px-4 py-2.5 text-base'
      }
    };
    return sizes[size];
  };

  const sizeClasses = getSizeClasses();

  if (visibleActions.length === 0) {
    return null;
  }

  return (
    <div ref={dropdownRef} className={`relative inline-block text-left ${className}`}>
      <button
        ref={buttonRef}
        onClick={() => !disabled && setIsOpen(!isOpen)}
        disabled={disabled}
        className={`
          ${sizeClasses.button}
          inline-flex items-center justify-center
          text-gray-400 hover:text-gray-600
          rounded-lg hover:bg-gray-100
          transition-colors duration-200
          disabled:opacity-50 disabled:cursor-not-allowed
          focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500
        `}
        title="More actions"
      >
        <MoreVertical className={sizeClasses.icon} />
      </button>

      {isOpen && (
        <div className={`
          absolute right-0 z-50 mt-1
          ${sizeClasses.dropdown}
          bg-white rounded-lg shadow-lg border border-gray-200
          py-1 max-h-64 overflow-y-auto
          animate-in fade-in-0 zoom-in-95 slide-in-from-top-2
          duration-200
        `}>
          {visibleActions.map((action, index) => {
            const IconComponent = action.icon || iconMap[action.id as keyof typeof iconMap];
            const showSeparator = action.separator && index > 0;
            
            return (
              <React.Fragment key={action.id}>
                {showSeparator && (
                  <hr className="my-1 border-gray-200" />
                )}
                <button
                  onClick={() => handleActionClick(action)}
                  disabled={action.disabled}
                  className={`
                    ${sizeClasses.item}
                    w-full text-left flex items-center gap-3
                    transition-colors duration-150
                    disabled:opacity-50 disabled:cursor-not-allowed
                    ${getVariantClasses(action.variant)}
                  `}
                >
                  {IconComponent && (
                    <IconComponent className={`${sizeClasses.icon} flex-shrink-0`} />
                  )}
                  <span className="truncate">{action.label}</span>
                </button>
              </React.Fragment>
            );
          })}
        </div>
      )}
    </div>
  );
};

// Pre-configured action creators for common use cases
export const createCommonActions = {
  view: (onClick: () => void, disabled?: boolean): ActionItem => ({
    id: 'view',
    label: 'View Details',
    icon: Eye,
    onClick,
    variant: 'primary',
    disabled
  }),

  edit: (onClick: () => void, disabled?: boolean): ActionItem => ({
    id: 'edit',
    label: 'Edit',
    icon: Edit,
    onClick,
    variant: 'default',
    disabled
  }),

  delete: (onClick: () => void, disabled?: boolean): ActionItem => ({
    id: 'delete',
    label: 'Delete',
    icon: Trash2,
    onClick,
    variant: 'danger',
    disabled,
    separator: true
  }),

  toggleStatus: (isActive: boolean, onClick: () => void, disabled?: boolean): ActionItem => ({
    id: isActive ? 'deactivate' : 'activate',
    label: isActive ? 'Deactivate' : 'Activate',
    icon: isActive ? UserX : UserCheck,
    onClick,
    variant: isActive ? 'warning' : 'success',
    disabled
  }),

  feature: (isFeatured: boolean, onClick: () => void, disabled?: boolean): ActionItem => ({
    id: isFeatured ? 'unfeature' : 'feature',
    label: isFeatured ? 'Remove from Featured' : 'Mark as Featured',
    icon: isFeatured ? StarOff : Star,
    onClick,
    variant: isFeatured ? 'secondary' : 'warning',
    disabled
  }),

  approve: (onClick: () => void, disabled?: boolean): ActionItem => ({
    id: 'approve',
    label: 'Approve',
    icon: CheckCircle,
    onClick,
    variant: 'success',
    disabled
  }),

  reject: (onClick: () => void, disabled?: boolean): ActionItem => ({
    id: 'reject',
    label: 'Reject',
    icon: XCircle,
    onClick,
    variant: 'danger',
    disabled
  }),

  duplicate: (onClick: () => void, disabled?: boolean): ActionItem => ({
    id: 'copy',
    label: 'Duplicate',
    icon: Copy,
    onClick,
    variant: 'default',
    disabled
  }),

  download: (onClick: () => void, disabled?: boolean): ActionItem => ({
    id: 'download',
    label: 'Download',
    icon: Download,
    onClick,
    variant: 'default',
    disabled
  })
};

export default ActionsDropdown;